import type {
  CalculationType,
  RmCalculationType,
  RrpCalculationType,
} from '@/api';
import { LabeledField } from '@/components/form/labeled-field';
import NumericTextField from '@/components/form/numeric-text-field';
import {
  invalidNumberHelpText,
  requiredHelpText,
} from '@/components/form/validation-help-text';
import { extractDropdownValue } from '@/utils/dropdown';
import { Dropdown, Icon } from '@lego/connect-components-react';
import {
  CALCULATION_TYPE_LABELS,
  type CalculationMethodsValidation,
  type PriceUnitCalculationSettings,
  type PriceUnitSettingsTouchedField,
  RM_CALCULATION_TYPE_LABELS,
  RRP_CALCULATION_TYPE_LABELS,
} from '../types';

type CalculationMethodsSectionProps = {
  priceUnitSettings: PriceUnitCalculationSettings;
  calculationTypes: CalculationType[];
  rmCalculationTypes: RmCalculationType[];
  rrpCalculationTypes: RrpCalculationType[];
  rmRrpDisabled: boolean;
  isCalculationTypeLicensedProducts: boolean;
  isCalculationTypeTargetDrivenLpAndRrp: boolean;
  validation: CalculationMethodsValidation;
  onFieldChange: (field: string, value: string | boolean | undefined) => void;
  onFieldBlur: (field: PriceUnitSettingsTouchedField) => void;
  onCalculationTypeChange: (value: string | undefined) => void;
};

const renderCalculationTypeOptions = <T extends string>(
  values: T[],
  labels: Record<T, string>,
) =>
  values.map((value) => (
    <Dropdown.Option key={value} value={value}>
      {labels[value]}
    </Dropdown.Option>
  ));

export const CalculationMethodsSection = ({
  priceUnitSettings,
  calculationTypes,
  rmCalculationTypes,
  rrpCalculationTypes,
  rmRrpDisabled,
  isCalculationTypeLicensedProducts,
  isCalculationTypeTargetDrivenLpAndRrp,
  validation,
  onFieldChange,
  onFieldBlur,
  onCalculationTypeChange,
}: CalculationMethodsSectionProps) => {
  const {
    isMissingCalculationType,
    isMissingRm,
    isMissingRrp,
    isMissingBreakEven,
    isMissingTargetRrp,
  } = validation;
  const {
    calculationType,
    breakEvenFactor,
    targetRrpIndex,
    rmPercentage,
    rrpType,
  } = priceUnitSettings;
  const rmDisabled = rmRrpDisabled || isCalculationTypeTargetDrivenLpAndRrp;

  return (
    <>
      <span className="label-lg-medium">Calculation Methods</span>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: '16px',
          width: '100%',
        }}
      >
        <LabeledField
          label="Main Calculation Method"
          helpText={isMissingCalculationType ? requiredHelpText : undefined}
        >
          <Dropdown
            fullWidth
            invalid={isMissingCalculationType}
            value={calculationType ? [calculationType] : []}
            onChange={(_event, value) => {
              const selectedValue = extractDropdownValue(value);
              onCalculationTypeChange(selectedValue || undefined);
            }}
            onBlur={() => onFieldBlur('calculationType')}
          >
            {renderCalculationTypeOptions(
              calculationTypes,
              CALCULATION_TYPE_LABELS,
            )}
          </Dropdown>
        </LabeledField>
        <LabeledField
          label="RM Calculation Method"
          disabled={rmDisabled}
          helpText={isMissingRm ? requiredHelpText : undefined}
        >
          <Dropdown
            fullWidth
            disabled={rmDisabled}
            invalid={isMissingRm}
            value={rmPercentage ? [rmPercentage] : []}
            onChange={(_event, value) =>
              onFieldChange(
                'rmPercentage',
                extractDropdownValue(value) || undefined,
              )
            }
            onBlur={() => onFieldBlur('rmPercentage')}
          >
            {renderCalculationTypeOptions(
              rmCalculationTypes,
              RM_CALCULATION_TYPE_LABELS,
            )}
          </Dropdown>
        </LabeledField>
        <LabeledField
          label="RRP Calculation Method"
          disabled={rmRrpDisabled}
          helpText={isMissingRrp ? requiredHelpText : undefined}
        >
          <Dropdown
            fullWidth
            disabled={rmRrpDisabled}
            invalid={isMissingRrp}
            value={rrpType ? [rrpType] : []}
            onChange={(_event, value) =>
              onFieldChange('rrpType', extractDropdownValue(value) || undefined)
            }
            onBlur={() => onFieldBlur('rrpType')}
          >
            {renderCalculationTypeOptions(
              rrpCalculationTypes,
              RRP_CALCULATION_TYPE_LABELS,
            )}
          </Dropdown>
        </LabeledField>
      </div>
      {(isCalculationTypeLicensedProducts ||
        isCalculationTypeTargetDrivenLpAndRrp) && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
          <Icon
            name="arrow-turn-down-right"
            aria-label="arrow-turn-down-right"
            size="medium"
          />
          <LabeledField
            label="Target RRP Index"
            tooltipText="Target RRP index, entered as a percentage of the price. Enter 100 for 100% of the price."
            helpText={isMissingTargetRrp ? invalidNumberHelpText : undefined}
          >
            <NumericTextField
              validationState={isMissingTargetRrp ? 'invalid' : undefined}
              value={targetRrpIndex ?? ''}
              onChange={(_e, value) => onFieldChange('targetRrpIndex', value)}
              onBlur={() => onFieldBlur('targetRrpIndex')}
              required
            />
          </LabeledField>
          {isCalculationTypeLicensedProducts && (
            <LabeledField
              label="Break Even Factor"
              tooltipText="Break even factor for licensed products, entered as a percentage of the price. Enter 100 for 100% of the price."
              helpText={isMissingBreakEven ? invalidNumberHelpText : undefined}
            >
              <NumericTextField
                validationState={isMissingBreakEven ? 'invalid' : undefined}
                value={breakEvenFactor ?? ''}
                onChange={(_e, value) =>
                  onFieldChange('breakEvenFactor', value)
                }
                onBlur={() => onFieldBlur('breakEvenFactor')}
                required
              />
            </LabeledField>
          )}
        </div>
      )}
    </>
  );
};
